import { ReactNode, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AppScope, appScopeUtils } from '../appScope.types';
import { AppScopeProvider } from './AppScopeProvider';
import { AppScopeContext } from './AppScopeContext';
import { isInitializedAppScope } from './internalAppScope';

type UiState = {
  bookingSidebarAppScope: AppScope | null;
};

/**
 * Same resolution as {@link PortaledModalAppScope}: stash from
 * {@link openBookingSidebar} wins; no stash → organization from ambient
 * context (never infer nested browsing scope from the live URL).
 *
 * Separate Redux key (`bookingSidebarAppScope`) so conflict modals do not
 * overwrite the sidebar opener lens.
 */
export function PortaledBookingSidebarAppScope({
  children
}: {
  children: ReactNode;
}) {
  const contextScope = useContext(AppScopeContext);
  const stashedAppScope = useSelector(
    (state: { ui: UiState }) => state.ui.bookingSidebarAppScope
  );

  const scope = useMemo((): AppScope => {
    if (stashedAppScope) {
      appScopeUtils.assertValid(stashedAppScope);
      return stashedAppScope;
    }
    if (contextScope && isInitializedAppScope(contextScope)) {
      return {
        level: 'organization',
        organizationId: contextScope.organizationId
      };
    }
    throw new Error(
      'AppScope is not initialized yet — booking sidebar mounted before organizationId was available'
    );
  }, [stashedAppScope, contextScope]);

  return <AppScopeProvider scope={scope}>{children}</AppScopeProvider>;
}
